import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import usePluginEntities from "hooks/usePluginEntities";
import { Row, Col, Checkbox } from "components/bootstrap";
import FieldSortSelect from "views/components/widgets/FieldSortSelect";
import SortDirectionSelect from "views/components/widgets/SortDirectionSelect";
import DescriptionBox from "views/components/aggregationbuilder/DescriptionBox";
import DecoratorSidebar from "views/components/messagelist/decorators/DecoratorSidebar";
import { HoverForHelp } from "components/common";
import { defaultCompare } from "logic/DefaultCompare";
import SaveOrCancelButtons from "views/components/widgets/SaveOrCancelButtons";
import StickyBottomActions from "views/components/widgets/StickyBottomActions";
import FieldsConfiguration from "views/components/widgets/FieldsConfiguration";
const FullHeightRow = styled(Row)`
  height: 100%;
  padding-bottom: 15px;
  flex: 1;
`;
const FullHeightCol = styled(Col)`
  height: 100%;
`;
const PreviewOptionCheckbox = styled(Checkbox)`
  label {
    display: flex;
    justify-content: space-between;
  }
`;
const _onFieldSelectionChanged = (fields, config, onChange) => {
  const newConfig = config.toBuilder().fields(fields).build();
  return onChange(newConfig);
};
const _onSortChange = (sort, config, onChange) => {
  const newConfig = config.toBuilder().sort(sort).build();
  return onChange(newConfig);
};
const _onSortDirectionChange = (direction, config, onChange) => {
  const newConfig = config.toBuilder().sort(config.sort.map((sort) => sort.toBuilder().direction(direction).build())).build();
  return onChange(newConfig);
};
const EditMessageList = ({
  children,
  config,
  fields,
  onChange,
  onCancel
}) => {
  const { t } = useTranslation("widgets");
  const { sort } = config;
  const [sortDirection] = (sort || []).map((s) => s.direction);
  const onDecoratorsChange = (newDecorators) => onChange(config.toBuilder().decorators(newDecorators).build());
  const messagePreviewOptions = usePluginEntities("views.components.widgets.messageTable.previewOptions");
  const sortedMessagePreviewOptions = messagePreviewOptions.sort((o1, o2) => defaultCompare(o1.sort, o2.sort));
  return /* @__PURE__ */ React.createElement(FullHeightRow, null, /* @__PURE__ */ React.createElement(FullHeightCol, { md: 3 }, /* @__PURE__ */ React.createElement(StickyBottomActions, { actions: /* @__PURE__ */ React.createElement(SaveOrCancelButtons, { onCancel }), alignActionsAtBottom: true }, /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("edit_message_list.fields") }, /* @__PURE__ */ React.createElement(
    FieldsConfiguration,
    {
      onChange: (newFields) => _onFieldSelectionChanged(newFields, config, onChange),
      selectedFields: config.fields
    }
  )), /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("edit_message_list.message_preview") }, sortedMessagePreviewOptions.map((option) => /* @__PURE__ */ React.createElement(
    PreviewOptionCheckbox,
    {
      key: option.title,
      checked: option.isChecked(config),
      onChange: () => option.onChange(config, onChange),
      disabled: option.isDisabled(config)
    },
    option.title,
    option.help && /* @__PURE__ */ React.createElement(HoverForHelp, { title: option.title }, option.help)
  ))), /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("edit_message_list.sorting") }, /* @__PURE__ */ React.createElement(FieldSortSelect, { fields, sort, onChange: (data) => _onSortChange(data, config, onChange) })), /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("edit_message_list.direction") }, /* @__PURE__ */ React.createElement(
    SortDirectionSelect,
    {
      disabled: !sort || sort.length === 0,
      direction: sortDirection && sortDirection.direction,
      onChange: (data) => _onSortDirectionChange(data, config, onChange)
    }
  )), /* @__PURE__ */ React.createElement(DescriptionBox, { description: t("edit_message_list.decorators") }, /* @__PURE__ */ React.createElement(DecoratorSidebar, { decorators: config.decorators, onChange: onDecoratorsChange })))), /* @__PURE__ */ React.createElement(FullHeightCol, { md: 9 }, children));
};
export default EditMessageList;
